import type { ReactNode } from "react";
import { Switch as AriaSwitch, type SwitchProps as AriaSwitchProps, Text } from "react-aria-components";
import { cx } from "@/lib/cx";

export interface SwitchProps extends Omit<AriaSwitchProps, "children" | "className"> {
  children: ReactNode;
  description?: ReactNode;
  className?: string;
}

export function Switch({ children, description, className, ...props }: SwitchProps) {
  return (
    <AriaSwitch
      className={cx("group flex items-start gap-3 text-sm font-medium text-fg data-disabled:opacity-50", className)}
      {...props}
    >
      {({ isSelected }) => (
        <>
          <span
            aria-hidden="true"
            className={cx(
              "mt-0.5 flex h-4 w-7 shrink-0 items-center rounded-full border p-px transition-colors duration-(--dur-base)",
              isSelected
                ? "border-accent bg-accent"
                : "border-input-border bg-hover group-data-hovered:border-input-border-hover",
            )}
          >
            <span
              className={cx(
                "block size-3 rounded-full shadow-[0_1px_1px_var(--shadow-color)] transition-transform duration-(--dur-base) ease-out",
                isSelected ? "translate-x-3 bg-accent-fg" : "translate-x-0 bg-fg-muted",
              )}
            />
          </span>
          <span className="flex min-w-0 flex-col">
            <span>{children}</span>
            {description ? (
              <Text slot="description" className="mt-0.5 text-xs font-normal text-fg-muted">
                {description}
              </Text>
            ) : null}
          </span>
        </>
      )}
    </AriaSwitch>
  );
}
